import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PlusIcon, ChevronRightIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useWorkspace } from '../store/workspaceStore';
import { STAGE_META, ProposalStage } from '../types';
import { Topbar } from '../components/layout/Topbar';
import { Avatar } from '../components/common/Avatar';
import { NewRequestModal } from '../components/proposals/NewRequestModal';
const FILTERS: {
  key: 'all' | ProposalStage;
  label: string;
}[] = [{
  key: 'all',
  label: 'All'
}, {
  key: 'requested',
  label: 'Requested'
}, {
  key: 'in_progress',
  label: 'In progress'
}, {
  key: 'om_sent',
  label: 'OM sent'
}, {
  key: 'ela_signed',
  label: 'ELA signed'
}, {
  key: 'listed',
  label: 'Listed'
}, {
  key: 'live',
  label: 'Live'
}];
export function ProposalsBoard() {
  const proposals = useWorkspace(s => s.proposals);
  const personById = useWorkspace(s => s.personById);
  const navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const [filter, setFilter] = useState<'all' | ProposalStage>('all');
  const filtered = useMemo(() => filter === 'all' ? proposals : proposals.filter(p => p.stage === filter), [proposals, filter]);
  return <div className="flex min-h-full flex-1 flex-col" data-id="element-300" data-mp="f54aka3bkr5b">
      <Topbar title="Proposal Workflow" subtitle="Every broker request and its deal checklist, in one place." action={<button onClick={() => setModal(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-accent px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand" data-id="element-302" data-mp="ouxkl6ntcy7g">
            <PlusIcon className="h-4 w-4" data-id="element-303" data-mp="xx6sdorp6dh5" /> New Request
          </button>} data-id="element-301" data-mp="tujxfezqccfq" />

      <div className="mp-scroll flex-1 overflow-y-auto p-6" data-id="element-304" data-mp="34ndae551iacu">
        {/* Filter tabs */}
        <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-305" data-mp="2zp205tau1xu3">
          {FILTERS.map(f => <button key={f.key} onClick={() => setFilter(f.key)} className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${filter === f.key ? 'bg-brand text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`} data-id="element-306" data-mp="13lnjwteezgfy">
              {f.label}
            </button>)}
        </div>

        {/* List */}
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card" data-id="element-307" data-mp="1miosspb3a0p6">
          {/* header row */}
          <div className="hidden grid-cols-[1fr_130px_120px_140px_120px_40px] items-center gap-3 border-b border-slate-100 bg-slate-50 px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 md:grid" data-id="element-308" data-mp="1rghub51g66j4">
            <span data-id="element-309" data-mp="259zij352w7fh">Property</span>
            <span data-id="element-310" data-mp="25b0s136g4lv3">Type</span>
            <span data-id="element-311" data-mp="25bso0f3ygllw">Stage</span>
            <span data-id="element-312" data-mp="2a92apmzymj61">Progress</span>
            <span data-id="element-313" data-mp="2a8arzcrgofx6">Requested by</span>
            <span data-id="element-314" data-mp="2a9bzx8d6kmao" />
          </div>

          {filtered.length === 0 && <p className="px-5 py-10 text-center text-sm text-slate-400" data-id="element-315" data-mp="3urwbswb3uxlr">No requests in this stage.</p>}

          {filtered.map((p, idx) => {
          const broker = personById(p.requestedById);
          const done = p.checklist.filter(c => c.status === 'done').length;
          const total = p.checklist.length;
          const pct = Math.round(done / total * 100);
          const stage = STAGE_META[p.stage];
          return <motion.button key={p.id} initial={{
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.03
          }} onClick={() => navigate(`/proposals/${p.id}`)} className="grid w-full grid-cols-1 items-center gap-3 border-b border-slate-100 px-5 py-4 text-left last:border-0 hover:bg-slate-50 md:grid-cols-[1fr_130px_120px_140px_120px_40px]" data-id="element-316" data-mp="davb13b3eltm" data-mpi={idx}>
                <div className="min-w-0" data-id="element-317" data-mp="w7mnotw4q2op">
                  <p className="truncate text-sm font-semibold text-slate-800" data-id="element-318" data-mp="fsx10sbdqgpu">{p.property}</p>
                  <p className="truncate text-xs text-slate-400" data-id="element-319" data-mp="ktb8o992jonc">{p.address}</p>
                </div>
                <span className="w-fit rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600" data-id="element-320" data-mp="6u1rp7d1gqib">{p.deckType}</span>
                <span className={`w-fit rounded-full px-2.5 py-1 text-xs font-semibold ${stage.color}`} data-id="element-321" data-mp="bsmxdmb282e5">{stage.label}</span>
                <div data-id="element-322" data-mp="zkbl0pcxs2or">
                  <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-slate-500" data-id="element-323" data-mp="13k2gv7hbl905">
                    <span data-id="element-324" data-mp="psxl4gh2p0v3">{done}/{total}</span>
                    <span data-id="element-325" data-mp="pq1p4xoscm9a">{pct}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100" data-id="element-326" data-mp="uq7ci1o0vmlk">
                    <div className="h-full rounded-full bg-emerald-500" style={{
                  width: `${pct}%`
                }} data-id="element-327" data-mp="1ifs5lddw408k" />
                  </div>
                </div>
                <div className="flex items-center gap-2" data-id="element-328" data-mp="1n9cvei5bxnwo">
                  <Avatar person={broker} size={26} data-id="element-329" data-mp="1wl0v3145lo96" />
                  <div className="hidden lg:block" data-id="element-330" data-mp="1skqcmoz6d5df">
                    <p className="text-xs font-medium text-slate-600" data-id="element-331" data-mp="1sjyq03nk2hza">{broker?.name}</p>
                    <p className="text-[10px] text-slate-400" data-id="element-332" data-mp="26b3oh2f6t481">{formatDistanceToNow(new Date(p.requestedAt), {
                    addSuffix: true
                  })}</p>
                  </div>
                </div>
                <ChevronRightIcon className="hidden h-4 w-4 justify-self-end text-slate-300 md:block" data-id="element-333" data-mp="1dmy0m6su6z3z" />
              </motion.button>;
        })}
        </div>
      </div>

      {modal && <NewRequestModal onClose={() => setModal(false)} onCreated={id => navigate(`/proposals/${id}`)} data-id="element-334" data-mp="1nm6qango5rkj" />}
    </div>;
}